/**
 * Strapi Service - Centralized API Communication
 * Handles REST and GraphQL requests to Strapi CMS
 */

class StrapiService {
  constructor(config = {}) {
    this.baseUrl = config.baseUrl || 'http://localhost:1337';
    this.apiToken = config.apiToken || null;
    this.timeout = config.timeout || 10000;
    this.enableCache = config.enableCache !== false;
    this.cache = new Map();
    this.cacheExpiry = config.cacheExpiry || 5 * 60 * 1000; // 5 minutos
  }

  /**
   * Configura headers padrão para requisições
   */
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (this.apiToken) {
      headers['Authorization'] = `Bearer ${this.apiToken}`;
    }

    return headers;
  }

  /**
   * Gera chave de cache baseada na URL
   */
  getCacheKey(url, params = {}) {
    return `${url}?${JSON.stringify(params)}`;
  }

  /**
   * Verifica se cache é válido
   */
  isCacheValid(cacheEntry) {
    if (!cacheEntry) return false;
    return Date.now() - cacheEntry.timestamp < this.cacheExpiry;
  }

  /**
   * Busca dados do cache
   */
  getFromCache(key) {
    if (!this.enableCache) return null;

    const cached = this.cache.get(key);
    if (cached && this.isCacheValid(cached)) {
      return cached.data;
    }

    // Remove cache expirado
    if (cached) {
      this.cache.delete(key);
    }

    return null;
  }

  /**
   * Salva dados no cache
   */
  setCache(key, data) {
    if (!this.enableCache) return;

    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Limpa todo o cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Faz requisição fetch com timeout
   */
  async fetchWithTimeout(url, options = {}) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          ...this.getHeaders(),
          ...options.headers
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        throw new Error(`Request timeout after ${this.timeout}ms`);
      }

      throw error;
    }
  }

  /**
   * GET request to Strapi REST API
   * @param {string} endpoint - API endpoint (e.g., 'banners', 'articles')
   * @param {Object} params - Query parameters
   */
  async get(endpoint, params = {}) {
    // Constrói query string sem encodar valores especiais do Strapi (como *)
    const queryString = Object.entries(params)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');

    const url = `${this.baseUrl}/api/${endpoint}${queryString ? `?${queryString}` : ''}`;

    // Check cache first
    const cacheKey = this.getCacheKey(url, params);
    const cached = this.getFromCache(cacheKey);

    if (cached) {
      return cached;
    }

    try {
      const data = await this.fetchWithTimeout(url);
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * POST request to Strapi REST API
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Data to send
   */
  async post(endpoint, data = {}) {
    const url = `${this.baseUrl}/api/${endpoint}`;

    try {
      const response = await this.fetchWithTimeout(url, {
        method: 'POST',
        body: JSON.stringify({ data })
      });

      // Invalida cache relacionado
      this.invalidateCacheByEndpoint(endpoint);

      return response;
    } catch (error) {
      console.error(`[StrapiService] Error posting to ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * PUT request to Strapi REST API
   * @param {string} endpoint - API endpoint with ID
   * @param {Object} data - Data to update
   */
  async put(endpoint, data = {}) {
    const url = `${this.baseUrl}/api/${endpoint}`;

    try {
      const response = await this.fetchWithTimeout(url, {
        method: 'PUT',
        body: JSON.stringify({ data })
      });

      this.invalidateCacheByEndpoint(endpoint);

      return response;
    } catch (error) {
      console.error(`[StrapiService] Error updating ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * DELETE request to Strapi REST API
   * @param {string} endpoint - API endpoint with ID
   */
  async delete(endpoint) {
    const url = `${this.baseUrl}/api/${endpoint}`;

    try {
      const response = await this.fetchWithTimeout(url, {
        method: 'DELETE'
      });

      this.invalidateCacheByEndpoint(endpoint);

      return response;
    } catch (error) {
      console.error(`[StrapiService] Error deleting ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * GraphQL query
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   */
  async graphql(query, variables = {}) {
    const url = `${this.baseUrl}/graphql`;
    const cacheKey = this.getCacheKey(url, { query, variables });

    // Check cache
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const response = await this.fetchWithTimeout(url, {
        method: 'POST',
        body: JSON.stringify({ query, variables })
      });

      if (response.errors) {
        throw new Error(`GraphQL errors: ${JSON.stringify(response.errors)}`);
      }

      this.setCache(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('[StrapiService] GraphQL error:', error);
      throw error;
    }
  }

  /**
   * Invalida cache por endpoint
   */
  invalidateCacheByEndpoint(endpoint) {
    const endpointPath = endpoint.split('?')[0];

    for (const [key] of this.cache) {
      if (key.includes(endpointPath)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Busca com populate (relationships)
   * @param {string} endpoint - API endpoint
   * @param {Array|string} populate - Fields to populate
   */
  async getWithPopulate(endpoint, populate = '*') {
    const populateParam = Array.isArray(populate)
      ? populate.join(',')
      : populate;

    return this.get(endpoint, { populate: populateParam });
  }

  /**
   * Busca com filtros
   * @param {string} endpoint - API endpoint
   * @param {Object} filters - Strapi filters object
   */
  async getWithFilters(endpoint, filters = {}) {
    const params = {};

    // Converte filtros para formato Strapi v4
    Object.entries(filters).forEach(([key, value]) => {
      params[`filters[${key}][$eq]`] = value;
    });

    return this.get(endpoint, params);
  }

  /**
   * Busca com paginação
   * @param {string} endpoint - API endpoint
   * @param {number} page - Page number (1-indexed)
   * @param {number} pageSize - Items per page
   */
  async getPaginated(endpoint, page = 1, pageSize = 25) {
    return this.get(endpoint, {
      'pagination[page]': page,
      'pagination[pageSize]': pageSize
    });
  }

  /**
   * Verifica saúde da API
   */
  async healthCheck() {
    try {
      const response = await fetch(`${this.baseUrl}/_health`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000)
      });

      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Estatísticas do cache
   */
  getCacheStats() {
    let validEntries = 0;
    let expiredEntries = 0;

    for (const [, entry] of this.cache) {
      if (this.isCacheValid(entry)) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    }

    return {
      total: this.cache.size,
      valid: validEntries,
      expired: expiredEntries,
      enabled: this.enableCache
    };
  }
}

export default StrapiService;
